<template>
  <div class="staff-snapshot-container">
    <el-card class="search-card">
      <div slot="header">
        <span>{{ $t('staffSnapshot.searchCondition') }}</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input v-model="conditions.staffName" :placeholder="$t('staffSnapshot.staffNamePlaceholder')" clearable />
        </el-col>
        <el-col :span="6">
          <el-input v-model="conditions.category" :placeholder="$t('staffSnapshot.categoryPlaceholder')" clearable />
        </el-col>
        <el-col :span="6">
          <el-input v-model="conditions.address" :placeholder="$t('staffSnapshot.addressPlaceholder')" clearable />
        </el-col>
        <el-col :span="6">
          <el-input v-model="conditions.description" :placeholder="$t('staffSnapshot.descriptionPlaceholder')" clearable />
        </el-col>
      </el-row>
      <el-row :gutter="20" style="margin-top: 16px;">
        <el-col :span="12">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t('staffSnapshot.startTime')"
            :end-placeholder="$t('staffSnapshot.endTime')"
            range-separator="-"
            clearable
            style="width: 100%"
          />
        </el-col>
        <el-col :span="6">
          <el-button type="primary" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card style="margin-top: 16px;">
      <div slot="header">
        <span>{{ $t('staffSnapshot.pageTitle') }}</span>
        <el-button type="primary" size="small" style="float: right;" @click="handleAdd">{{ $t('staffSnapshot.add') }}</el-button>
      </div>
      <el-table :data="tableData" border v-loading="loading">
        <el-table-column :label="$t('staffSnapshot.photo')" width="130" align="center">
          <template slot-scope="scope">
            <div v-if="getPhotoUrls(scope.row.photoUrl).length > 0" style="position: relative; display: inline-block;">
              <el-image style="width: 60px; height: 60px; cursor: pointer;"
                :src="getPhotoUrls(scope.row.photoUrl)[0]"
                :preview-src-list="getPhotoUrls(scope.row.photoUrl)" fit="cover" />
              <span v-if="getPhotoUrls(scope.row.photoUrl).length > 1"
                style="position: absolute; bottom: 0; right: 0; background: rgba(0,0,0,0.6); color: #fff; font-size: 12px; padding: 0 4px; border-radius: 2px;">
                {{ getPhotoUrls(scope.row.photoUrl).length }}
              </span>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="staffName" :label="$t('staffSnapshot.staffName')" width="120" align="center" />
        <el-table-column prop="category" :label="$t('staffSnapshot.category')" width="100" align="center" />
        <el-table-column prop="address" :label="$t('staffSnapshot.address')" align="center" show-overflow-tooltip />
        <el-table-column prop="description" :label="$t('staffSnapshot.description')" align="center" show-overflow-tooltip />
        <el-table-column prop="createTime" :label="$t('staffSnapshot.createTime')" width="170" align="center" />
        <el-table-column :label="$t('staffSnapshot.action')" width="160" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">
              {{ $t('staffSnapshot.detail') }}
            </el-button>
            <el-button type="text" size="mini" style="color: #F56C6C" @click="handleDelete(scope.row)">
              {{ $t('staffSnapshot.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination style="margin-top: 16px; text-align: right;"
        :current-page="pagination.current" :page-sizes="[10, 20, 30, 50]" :page-size="pagination.size"
        :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </el-card>

    <add-staff-snapshot ref="addStaffSnapshot" @success="handleSearch" />

    <el-dialog :title="$t('staffSnapshot.detailTitle')" :visible.sync="detailVisible" width="700px">
      <div v-if="detailRow" style="text-align:center">
        <div v-if="getPhotoUrls(detailRow.photoUrl).length > 0" style="margin-bottom:16px">
          <el-image
            v-for="(url, idx) in getPhotoUrls(detailRow.photoUrl)"
            :key="idx"
            :src="url"
            :preview-src-list="getPhotoUrls(detailRow.photoUrl)"
            fit="contain"
            style="width: 200px; height: 200px; margin: 0 8px; border: 1px solid #eee"
          />
        </div>
        <el-descriptions :column="2" border>
          <el-descriptions-item :label="$t('staffSnapshot.staffName')">{{ detailRow.staffName }}</el-descriptions-item>
          <el-descriptions-item :label="$t('staffSnapshot.category')">{{ detailRow.category || '-' }}</el-descriptions-item>
          <el-descriptions-item :label="$t('staffSnapshot.address')">{{ detailRow.address || '-' }}</el-descriptions-item>
          <el-descriptions-item :label="$t('staffSnapshot.createTime')">{{ detailRow.createTime }}</el-descriptions-item>
          <el-descriptions-item :label="$t('staffSnapshot.description')" :span="2">{{ detailRow.description || '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getStaffSnapshotList, deleteStaffSnapshot } from '@/api/staff/staffSnapshotApi'
import AddStaffSnapshot from '@/components/staff/addStaffSnapshot.vue'

export default {
  name: 'StaffSnapshotList',
  components: { AddStaffSnapshot },
  data() {
    return {
      loading: false,
      tableData: [],
      detailVisible: false,
      detailRow: null,
      dateRange: [],
      conditions: {
        staffName: '',
        category: '',
        address: '',
        description: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      const params = {
        page: this.pagination.current,
        row: this.pagination.size
      }
      if (this.conditions.staffName) {
        params.staffName = this.conditions.staffName
      }
      if (this.conditions.category) {
        params.category = this.conditions.category
      }
      if (this.conditions.address) {
        params.address = this.conditions.address
      }
      if (this.conditions.description) {
        params.description = this.conditions.description
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0]
        params.endTime = this.dateRange[1]
      }
      const res = await getStaffSnapshotList(params)
      if (res.code === 0) {
        this.tableData = res.data || []
        this.pagination.total = res.total || 0
      } else {
        this.$message.error(res.msg || this.$t('common.operationFailed'))
      }
      this.loading = false
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchData()
    },
    handleReset() {
      this.conditions.staffName = ''
      this.conditions.category = ''
      this.conditions.address = ''
      this.conditions.description = ''
      this.dateRange = []
      this.pagination.current = 1
      this.fetchData()
    },
    handleAdd() {
      this.$refs.addStaffSnapshot.open()
    },
    handleDetail(row) {
      this.detailRow = row
      this.detailVisible = true
    },
    handleDelete(row) {
      this.$confirm(this.$t('staffSnapshot.deleteConfirm'), this.$t('common.tip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        const res = await deleteStaffSnapshot({ snapshotId: row.snapshotId })
        if (res.code === 0) {
          this.$message.success(this.$t('common.operationSuccess'))
          this.fetchData()
        } else {
          this.$message.error(res.msg || this.$t('common.operationFailed'))
        }
      }).catch(() => {})
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.fetchData()
    },
    getPhotoUrl(photoUrl) {
      if (!photoUrl) return '/img/noPhoto.jpg'
      if (photoUrl.indexOf('http') === 0) return photoUrl
      return '/callComponent/download/getFile/file?communityId=-1&fileId=' + photoUrl
    },
    getPhotoUrls(photoUrl) {
      if (!photoUrl) return []
      return photoUrl.split(',').map(u => this.getPhotoUrl(u.trim()))
    }
  }
}
</script>
